import type { User } from '../types/auth';
import { ReportTypeCategory, ReportTypeIdentifier } from '../types/reports';

/**
 * D-12: Es gibt genau EINE Stelle, die entscheidet, welche Meldungstypen ein Nutzer sieht --
 * `visibleReportTypes(user)`. Sie traegt beide Achsen: Rolle UND freigeschaltete Kategorie.
 *
 * Dieses Gating ist ausdruecklich reine Frontend-Komfortebene ("verstecken statt deaktivieren",
 * Muster aus App.tsx:71-93). Die harte Zugriffsgrenze bleibt
 * `backend/src/Doctrine/Extension/ReportFilterExtension.php`, das die Kategorie unabhaengig
 * davon fuer jeden Nutzer prueft. Wer einen versteckten Typ per URL erzwingt, bekommt vom Server
 * weiterhin nichts (T-25-06).
 *
 * Keine React-, Mantine- oder Hook-Importe in dieser Datei -- sie muss unter `environment: 'node'`
 * ladbar bleiben.
 */

const MARKET_REPORT_TYPES: ReportTypeIdentifier[] = [
  ReportTypeIdentifier.FORMULAR_AB,
  ReportTypeIdentifier.FORMULAR_C,
  ReportTypeIdentifier.FORMULAR_D,
];

const NON_MARKET_REPORT_TYPES: ReportTypeIdentifier[] = [
  ReportTypeIdentifier.ENVIRONMENTAL,
  ReportTypeIdentifier.ACCIDENT,
];

const ALL_REPORT_TYPES: ReportTypeIdentifier[] = [
  ...MARKET_REPORT_TYPES,
  ...NON_MARKET_REPORT_TYPES,
];

/**
 * Meldungstypen, die die Rolle des Nutzers grundsaetzlich erreicht -- noch OHNE Beruecksichtigung
 * der freigeschalteten Kategorien. Konsolidiert aus zwei bisher divergierenden Kopien
 * (`ReportFilters.tsx:66-83`, `ReportsPage.tsx:121-193`).
 *
 * IMPORTANT: Check isPlantEmployee FIRST because all users have ROLE_USER -- ein Werksmitarbeiter
 * traegt mindestens zwei Rollen gleichzeitig; wird `isTrustee` zuerst geprueft, sieht er die
 * falsche Teilmenge.
 */
export function rolesToReportTypes(user: User | null): ReportTypeIdentifier[] {
  const roles = user?.roles ?? [];

  const isPlantEmployee = roles.includes('ROLE_PLANT_EMPLOYEE');
  const isTrustee = roles.includes('ROLE_TRUSTEE');
  const isAdminOnly = roles.includes('ROLE_ADMIN');

  if (isPlantEmployee) {
    // Werksmitarbeiter sehen alle Typen -- das Backend filtert bereits auf ihre zugewiesenen Werke.
    return ALL_REPORT_TYPES;
  }

  if (isTrustee) {
    // Treuhaender sehen ausschliesslich Markttypen.
    return MARKET_REPORT_TYPES;
  }

  if (isAdminOnly || roles.includes('ROLE_USER')) {
    // Admin/User sehen ausschliesslich Nicht-Markttypen.
    return NON_MARKET_REPORT_TYPES;
  }

  // Kein erkanntes Rollenmuster: leeres Array. Die beiden Bestandskopien divergierten genau hier
  // -- `ReportFilters.tsx` fiel auf alle fuenf Typen zurueck, `ReportsPage.tsx` auf keinen. Diese
  // Funktion uebernimmt die Variante von `ReportsPage.tsx`: die Tabelle zeigt in diesem Fall
  // ohnehin nichts, ein Dropdown-Eintrag ohne Wirkung waere eine tote Option -- und die
  // restriktivere der beiden Varianten ist bei einer Sichtbarkeitsfrage die richtige Wahl.
  return [];
}

/**
 * Meldungstypen, die ein Nutzer tatsaechlich sehen soll: Rolle UND freigeschaltete Kategorie
 * muessen beide zutreffen. Ein Typ, dessen Kategorie nicht in `enabledReportCategories` steht,
 * kommt hier gar nicht vor -- er wird nicht als deaktivierter Eintrag zurueckgegeben (D-10).
 * Bleibt nach Rolle ∩ Kategorie nichts uebrig, ist das Ergebnis ein leeres Array (D-11).
 */
export function visibleReportTypes(user: User | null): ReportTypeIdentifier[] {
  const enabledCategories = user?.enabledReportCategories ?? [];

  return rolesToReportTypes(user).filter((type) =>
    enabledCategories.includes(ReportTypeCategory[type])
  );
}
